import { useLooseProductContextSelector } from 'src/components/restructure/product/contexts/product-context'
import type { Variation } from 'src/components/product-page/Selector/types'
import { useRef } from 'react'
import type {
  ProductQueryObject,
  StandardObject,
} from 'src/utils/amplitude/types/amplitudeObjects'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useMobile } from 'src/hooks/useMobile'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import type { Product } from 'src/components/restructure/product/types/product'

import { SizeGuide } from '../SizeGuide/SizeGuide'
import TiledSelector from '../TiledSelector/TiledSelector'
import { DropdownSelector } from '../DropdownSelector/DropdownSelector'
import SizeGuideRecommendation from '../SizeGuide/SizeGuideRecommendation'
import { getSizeGuideModalConfig } from '../SizeGuide/SizeGuideModalContent'

interface Props {
  variation: Variation
  onClickVariation: (name: any, value: string) => void
  standardObject: StandardObject | null
  productObject: ProductQueryObject | null
  isFirstFoldRevampEnabled?: boolean
  productFromProps?: Product
  currentSkuFromProps?: ProductItem
}

const DECATHLON_SELLER_ID = '1'
const DROPDOWN_NAME_LENGTH_THRESHOLD = 5

const STYLES = {
  dropdownWrapper: 'mt-5',
  revampMobileDropdown: 'mt-5 restructure-small-desktop:hidden',
  desktopFallback: 'hidden restructure-small-desktop:block',
  header: 'flex items-center justify-between',
  variationName: 'font-medium text-restructure-primary text-sm font-inter',
  variationNameDesktopOnly: 'hidden restructure-small-desktop:inline',
  selectedValueName: 'text-tertiary',
} as const

export const KEYWORDS_HIDDEN_COMPONENT = [
  'ÚNICO',
  'UNICO',
  'Único',
  'ÚNICA',
  'UNICA',
  'Única',
  'U',
  ',',
  '.',
  'B',
  'N/A',
  'UN',
  'variado',
  'Sem sabor',
  'SEM-SABOR',
  '3953',
  '3954',
]

const SizeSelectorRender = ({
  variation,
  showDropdownSelector,
  isFirstFoldRevampEnabled,
  onClickVariation,
  sendAmplitudeEvent,
  sortedVariationValues,
  variationName,
  product,
  currentSku,
}: {
  variation: Variation
  showDropdownSelector: boolean
  isFirstFoldRevampEnabled?: boolean
  onClickVariation: (name: string, value: string) => void
  sendAmplitudeEvent: (value: string) => void
  variationName: string
  product: Product
  currentSku: ProductItem
  sortedVariationValues: Array<{
    id: string
    name: string
    isAvailable: boolean
    selected: boolean
  }>
}) => {
  if (variation.values.length === 1) {
    return null
  }

  const shouldUseRevampMobileDropdown =
    isFirstFoldRevampEnabled && variationName === 'Tamanho'

  if (shouldUseRevampMobileDropdown) {
    return (
      <>
        <div className={STYLES.revampMobileDropdown}>
          <DropdownSelector
            onClickVariation={onClickVariation}
            variation={variation}
            origin="sidebar"
            currentSkuFromProps={currentSku}
            productFromProps={product}
          />
        </div>
        <div className={STYLES.desktopFallback}>
          <TiledSelector
            onClickVariation={onClickVariation}
            sendAmplitudeEvent={sendAmplitudeEvent}
            sortedVariationValues={sortedVariationValues}
            variation={variation}
            variationName={variationName}
          />
        </div>
      </>
    )
  }

  return showDropdownSelector ? (
    <div className={STYLES.dropdownWrapper}>
      <DropdownSelector
        onClickVariation={onClickVariation}
        variation={variation}
        origin="sidebar"
        currentSkuFromProps={currentSku}
        productFromProps={product}
      />
    </div>
  ) : (
    <TiledSelector
      onClickVariation={onClickVariation}
      sendAmplitudeEvent={sendAmplitudeEvent}
      sortedVariationValues={sortedVariationValues}
      variation={variation}
      variationName={variationName}
    />
  )
}

export function DefaultSelector({
  variation,
  onClickVariation,
  standardObject,
  productObject,
  isFirstFoldRevampEnabled = false,
  currentSkuFromProps,
  productFromProps,
}: Readonly<Props>) {
  const contextCurrentSku = useLooseProductContextSelector(
    (ctx) => ctx.currentSku
  )

  const contextProduct = useLooseProductContextSelector((ctx) => ctx.product)

  const currentSku = currentSkuFromProps ?? contextCurrentSku
  const product = productFromProps ?? contextProduct
  const {
    sellerDefault: { sellerId },
  } = currentSku ?? {}

  const oldSizeRef = useRef<string | null>(currentSku?.Tamanho?.[0] ?? null)

  const { isMobile } = useMobile()
  const { openModal } = useModalSheet()
  const modalConfig = getSizeGuideModalConfig(product, isMobile)

  const featureDetailsObject = getFeatureObject({
    locationOnPage: null,
    name: 'Tabela de tamanhos',
    section: 'produto',
  })

  const recommendation = product?.sizeDetails?.recommendation
  const hasSizeGuideTable = Boolean(modalConfig)

  const sellerIsDecathlon = sellerId === DECATHLON_SELLER_ID

  const variationName = variation.field.name

  const showRecommendation =
    variationName === 'Tamanho' && sellerIsDecathlon && recommendation

  const sortedVariationValues = [...variation.values].sort(
    (a, b) => a.position - b.position
  )

  const selectedValueName = variation.values.find(
    (value) => value.selected
  )?.name

  const shouldHideVariationHeader =
    isFirstFoldRevampEnabled && variationName === 'Tamanho'

  const shouldShowSizeGuide =
    variationName === 'Tamanho' && sellerIsDecathlon && hasSizeGuideTable

  const showDropdownSelector = variation.values.some(
    (value) => value.name.length > DROPDOWN_NAME_LENGTH_THRESHOLD
  )

  if (
    sortedVariationValues.length <= 1 &&
    KEYWORDS_HIDDEN_COMPONENT.includes(sortedVariationValues[0].name)
  ) {
    return null
  }

  const openSizeGuideModal = () => {
    if (!modalConfig) {
      return
    }

    openModal(modalConfig)

    sendProductInformationSelectedEvent({
      standardObject,
      productObject,
      clickedElement: 'Tabela de tamanhos',
      featureDetailsObject,
    })
  }

  const sendAmplitudeEvent = (variationToSelect: string) => {
    if (!standardObject || !productObject) {
      return
    }

    const selectedItem = product.items.find(
      (productItem) =>
        productItem.Tamanho?.[0] === variationToSelect &&
        productItem.isAvailable
    )

    if (!selectedItem || selectedItem === currentSku) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Product Size Selected',
      eventData: {
        ...standardObject,
        'product details': [{ ...productObject, size: variationToSelect }],
        'old size selected': oldSizeRef.current,
        'new size selected': variationToSelect,
      },
    })

    oldSizeRef.current = variationToSelect
  }

  return (
    <div>
      {(!shouldHideVariationHeader || shouldShowSizeGuide) && (
        <div className={STYLES.header}>
          {!shouldHideVariationHeader && (
            <span className={STYLES.variationName}>
              {variationName}{' '}
              <span className={STYLES.selectedValueName}>
                {selectedValueName ?? ''}
              </span>
            </span>
          )}

          {shouldHideVariationHeader && (
            <span
              className={`${STYLES.variationName} ${STYLES.variationNameDesktopOnly}`}
            >
              {variationName}{' '}
              <span className={STYLES.selectedValueName}>
                {selectedValueName ?? ''}
              </span>
            </span>
          )}

          {shouldShowSizeGuide && (
            <SizeGuide
              openSizeGuideModal={openSizeGuideModal}
              featureDetailsObject={featureDetailsObject}
              isFirstFoldRevampEnabled={isFirstFoldRevampEnabled}
            />
          )}
        </div>
      )}

      {showRecommendation && (
        <SizeGuideRecommendation
          recommendation={recommendation}
          openSizeGuideModal={openSizeGuideModal}
        />
      )}

      <SizeSelectorRender
        onClickVariation={onClickVariation}
        sendAmplitudeEvent={sendAmplitudeEvent}
        showDropdownSelector={showDropdownSelector}
        isFirstFoldRevampEnabled={isFirstFoldRevampEnabled}
        sortedVariationValues={sortedVariationValues}
        variation={variation}
        variationName={variationName}
        currentSku={currentSku}
        product={product}
      />
    </div>
  )
}
